import { Link, useLocation } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import DateModifiedIcon from '../../../components/DateModifiedIcon'
import { useTimesheetApprovalScreen } from './hooks/useTimesheetApprovalScreen'
import { formatHoursAndMinutes } from '../../../utils/formatDuration'
import { useAuth } from '../../../hooks/useAuth'
import ApprovalEmptyState from '../components/ApprovalEmptyState'

function statusChipClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'REJECTED') return 'bg-error-container text-error'
  return 'bg-primary-fixed text-primary'
}

export default function TimesheetApprovalScreen() {
  const location = useLocation()
  const { hasPrivilege } = useAuth()
  const canViewJob = hasPrivilege('JobView')
  const canViewTask = hasPrivilege('TaskView')
  const canViewProject = hasPrivilege('ProjectView')

  const { state, actions } = useTimesheetApprovalScreen()

  const selectableCount = state.rows.filter((r) => r.approvalStatus === 'PENDING' && r.canApprove).length
  const isAllSelected = selectableCount > 0 && state.selectedIds.size >= selectableCount

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 rounded-[2rem] bg-surface-container-low px-8 py-8">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Approval Console</p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Timesheet Approvals</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-on-surface-variant md:text-lg">
          Review submitted project timesheets and approve billable effort before finance lock.
        </p>
      </header>

      <div className="mb-6">
        <SharedFilters
          showSearch={true}
          searchField="User Name"
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          searchFieldOptions={[{ value: 'User Name', label: 'User Name' }]}
          showStatus={true}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={[
            { value: 'PENDING', label: 'Pending' },
            { value: 'all', label: 'All Statuses' },
            { value: 'APPROVED', label: 'Approved' },
            { value: 'REJECTED', label: 'Rejected' },
          ]}
          visibleAccordionFilters={['agent', 'dateRange']}
          agentFilter={state.userFilter}
          setAgentFilter={actions.setUserFilter}
          agentOptions={state.userOptions.map((option) => ({
            value: String(option.id),
            label: option.name || `User #${option.id}`,
          }))}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          tools={[
            {
              key: 'multiSelect',
              label: state.isMultiSelectMode ? 'Cancel Selection' : 'Select Multiple',
              icon: state.isMultiSelectMode ? 'close' : 'checklist',
              iconClassName: state.isMultiSelectMode ? 'text-error' : 'text-primary',
            }
          ]}
          onToolSelect={(key) => {
            if (key === 'multiSelect') {
              actions.toggleMultiSelectMode();
            }
          }}
        />
      </div>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      {state.actionError ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.actionError}</div>
      ) : null}

      {state.actionMessage ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.actionMessage}</div>
      ) : null}

      <section className="overflow-hidden rounded-3xl bg-surface-container-low relative">
        {state.isMultiSelectMode && (
          <div className="bg-surface-container-highest border-b border-outline-variant/30 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-on-surface">
                {state.selectedIds.size} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={actions.bulkApprove}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-6 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={actions.bulkReject}
                disabled={state.selectedIds.size === 0}
                className="rounded-full bg-error-container hover:bg-error-container/80 px-6 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={actions.selectAll}
                disabled={selectableCount === 0}
                className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-2 text-xs font-bold uppercase tracking-wider text-white transition shadow-xs"
              >
                {isAllSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/40">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Timesheet No</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Submitted By</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Job</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Task</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Hours Logged</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-center">Actions</th>
                {state.isMultiSelectMode && (
                  <th className="px-6 py-5 text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant text-right">Select</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {state.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading approval queue...
                  </td>
                </tr>
              ) : !state.error && state.rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    <ApprovalEmptyState />
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const isPending = row.approvalStatus === 'PENDING'
                  const isBusy = state.activeActionId === row.id
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-surface-container-lowest">
                      <td className="px-6 py-5 text-base font-bold text-primary">{row.timesheetNo}</td>
                      <td className="px-6 py-5 text-base text-on-surface">
                        <div className="flex items-center gap-1.5">
                          <span>{row.entryDate}</span>
                          <DateModifiedIcon
                            hasDateChanges={row.hasDateChanges}
                            module="TIMESHEET"
                            entityId={row.id}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-5 text-base font-semibold text-on-surface">{row.userName}</td>
                      <td className="px-6 py-5">
                        {canViewJob && row.jobId ? (
                          <Link
                            to={`/nerve/job/entry?id=${row.jobId}&mode=view`}
                            className="text-base font-semibold text-primary hover:underline"
                            title="View Job Details"
                          >
                            {row.jobName}
                          </Link>
                        ) : (
                          <div className="text-base text-on-surface">{row.jobName}</div>
                        )}
                        <div className="text-xs text-on-surface-variant mt-0.5">
                          {canViewProject && row.projectId ? (
                            <Link
                              to={`/nerve/project/detail?id=${row.projectId}`}
                              className="hover:text-primary hover:underline"
                              title="View Project Details"
                            >
                              {row.projectName}
                            </Link>
                          ) : (
                            row.projectName
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-base text-on-surface">
                        {canViewTask && row.taskId ? (
                          <Link
                            to={`/nerve/task/entry?id=${row.taskId}&mode=view`}
                            className="font-semibold text-primary hover:underline"
                            title="View Task Details"
                          >
                            {row.taskName}
                          </Link>
                        ) : (
                          row.taskName
                        )}
                      </td>
                      <td className="px-6 py-5 text-base font-bold text-on-surface">{formatHoursAndMinutes(row.hoursLogged)}</td>
                      <td className="px-6 py-5">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${statusChipClass(row.approvalStatus)}`}>
                          {row.approvalStatus}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-2">
                          {!state.isMultiSelectMode && (
                            <>
                              <Link
                                to={`/nerve/timesheet/entry?id=${row.id}&mode=view`}
                                state={{ returnTo: `${location.pathname}${location.search}` }}
                                className="rounded-full bg-primary-fixed p-2 text-primary"
                                title="Preview"
                              >
                                <span className="material-symbols-outlined text-base">visibility</span>
                              </Link>

                              {row.canApprove && isPending ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => actions.approveTimesheet(row.id)}
                                    className="rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50"
                                  >
                                    {isBusy ? 'Working...' : 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isBusy}
                                    onClick={() => actions.rejectTimesheet(row.id)}
                                    className="rounded-full bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50"
                                  >
                                    {isBusy ? 'Working...' : 'Reject'}
                                  </button>
                                </>
                              ) : (
                                <span></span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      {state.isMultiSelectMode && (
                        <td className="px-6 py-5 text-right">
                          {row.canApprove && isPending ? (
                            <input
                              type="checkbox"
                              checked={state.selectedIds.has(row.id)}
                              onChange={() => actions.toggleSelection(row.id)}
                              className="h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              disabled
                              className="h-5 w-5 rounded border-outline-variant opacity-30 cursor-not-allowed"
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-6 py-5">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart}-{state.rangeEnd} of {state.totalCount} timesheet entries
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-4">
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Pending</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.pending}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Approved</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.approved}</h3>
        </article>
        <article className="rounded-2xl bg-surface-container-low p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Rejected</p>
          <h3 className="mt-2 text-4xl font-black text-on-surface">{state.totals.rejected}</h3>
        </article>
        <article className="rounded-2xl bg-primary p-6 text-on-primary">
          <p className="text-xs font-bold uppercase tracking-widest text-primary-fixed-dim">Total Logged (View)</p>
          <h3 className="mt-2 text-4xl font-black">{formatHoursAndMinutes(state.totals.totalHours)}</h3>
        </article>
      </section>
    </div>
  )
}
